/**
 * AI search API client for the AI service's /search endpoints.
 * Defines the SearchQuery/result/stream-event types, a plain POST /search
 * call via aiApi, and searchStream, which POSTs to /search/stream with fetch
 * and parses the SSE response (per-domain results, verification, ranking,
 * colors, done/error events), returning an abort function.
 * Consumed by the SearchPage.
 */
import { aiApi } from "@/lib/axios";

export interface SearchQuery {
    category: string;
    sex: string | null;
    category_type: string | null;
    query: string;
    search_type: "exact" | "similar" | "matching";
    min_price?: number;
    max_price?: number;
    image_url?: string;
}

export interface SearchSingleResult {
    title: string;
    description: string | null;
    size: string | null;
    price: string;
    image_url: string;
    product_url: string;
}

export interface SearchDomainResult {
    results_url: string;
    results: SearchSingleResult[];
}

export interface ColorPaletteEntry {
    name: string;
    hex: string;
}

export interface ColorsData {
    palette: ColorPaletteEntry[];
    results: Record<string, string>; // product_url → color name
}

export interface SearchStreamEvent {
    scraper_operational?: boolean;
    domain?: string;
    results?: SearchDomainResult;
    ranking_started?: boolean;
    verified?: string[];       // product_urls that passed verification
    ranking?: string[];        // product_urls in ranked order
    ranking_error?: string;
    colors?: ColorsData;
    done?: boolean;
    error?: string;
}

export const SearchAPI = {
    search: async (query: SearchQuery) => {
        const { data } = await aiApi.post(`/search`, query);
        return data;
    },

    searchStream: (
        query: SearchQuery,
        onResult: (event: SearchStreamEvent) => void,
        onError: (error: Error) => void,
        onComplete: () => void
    ): (() => void) => {
        const controller = new AbortController();

        const baseUrl = aiApi.defaults.baseURL || "";

        fetch(`${baseUrl}/search/stream`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(query),
            signal: controller.signal,
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error("No response body");
                }

                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });

                    // Parse SSE events from buffer
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || ""; // Keep incomplete line in buffer

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            try {
                                const data = JSON.parse(line.slice(6)) as SearchStreamEvent;
                                if (data.done) {
                                    onComplete();
                                } else if (data.error) {
                                    onError(new Error(data.error));
                                } else {
                                    onResult(data);
                                }
                            } catch {
                                // Skip malformed JSON
                            }
                        }
                    }
                }
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    onError(error);
                }
            });

        // Return abort function
        return () => controller.abort();
    },
};
